/**
 * 势力模板定义
 * 包含所有预定义的历史势力模板
 */

import type { FactionTemplate } from '../types/faction'

// ============================================
// 商朝势力模板
// ============================================

export const SHANG_TEMPLATES: Record<string, FactionTemplate> = {
  // 商王室 - 玩家默认势力
  shang: {
    templateId: 'shang',
    baseProfile: {
      name: '商',
      type: 'royal',
      color: '#8B4513', // 青铜色
      emblem: '/emblems/shang.png',
      description: '商王室，统治中原的共主。拥有发达的青铜文明和甲骨文，通过内外服制度管理广大疆域。',
    },
    traitTemplates: [
      {
        traitId: 'bronze_civilization',
        name: '青铜文明',
        description: '青铜冶炼技术发达，青铜产量+20%，军事科技研发速度+10%',
        effects: [
          { type: 'production', target: 'bronze', value: 20 },
          { type: 'research', target: 'military_tech', value: 10 },
        ],
      },
      {
        traitId: 'oracle_bone',
        name: '甲骨文',
        description: '拥有成熟的文字系统，文化影响力+15%，祭祀效果+20%',
        effects: [
          { type: 'culture', target: 'influence', value: 15 },
          { type: 'ritual', target: 'effect', value: 20 },
        ],
      },
      {
        traitId: 'inner_outer_service',
        name: '内外服制度',
        description: '独特的管理制度，附庸税收+10%，附庸忠诚度+15',
        effects: [
          { type: 'tax', target: 'vassal', value: 10 },
          { type: 'loyalty', target: 'vassal', value: 15 },
        ],
      },
      {
        traitId: 'ancestor_worship',
        name: '祖先崇拜',
        description: '重视祖先祭祀，祭祀成本-15%，民心+10',
        effects: [
          { type: 'cost', target: 'ritual', value: -15 },
          { type: 'morale', target: 'population', value: 10 },
        ],
      },
    ],
    baseResources: {
      food: 5000,
      money: 3000,
      population: 20000,
      prestige: 100,
    },
    specialRules: {
      canBeVassalized: false,
      canDeclareIndependence: false,
      maxCities: 0,
      uniqueBuildings: ['ancestral_temple', 'oracle_hall', 'bronze_workshop'],
    },
    historicalBackground: {
      origin: '商始祖契，相传为帝喾之子，助禹治水有功，封于商。',
      culture: '崇尚鬼神，重视祭祀，青铜文明高度发达，甲骨文是重要文化遗产。',
      keyFigures: ['成汤', '盘庚', '武丁', '帝辛'],
      riseAndFall: '成汤灭夏建立商朝，盘庚迁殷中兴，武丁时期达到鼎盛，帝辛时期因对外战争和内乱而被周所灭。',
    },
  },

  // 周方 - 西方诸侯
  zhou: {
    templateId: 'zhou',
    baseProfile: {
      name: '周',
      type: 'vassal',
      color: '#228B22', // 森林绿
      emblem: '/emblems/zhou.png',
      description: '西方诸侯，位于陕西关中。农耕发达，礼乐文化兴盛，后期逐渐强大，最终灭商建立周朝。',
    },
    traitTemplates: [
      {
        traitId: 'agricultural_expertise',
        name: '农耕发达',
        description: '关中平原沃野千里，粮食产量+25%，人口增长+15%',
        effects: [
          { type: 'production', target: 'food', value: 25 },
          { type: 'growth', target: 'population', value: 15 },
        ],
      },
      {
        traitId: 'ritual_culture',
        name: '礼乐文化',
        description: '重视礼乐教化，文化影响力+20%，附庸忠诚度+10',
        effects: [
          { type: 'culture', target: 'influence', value: 20 },
          { type: 'loyalty', target: 'vassal', value: 10 },
        ],
      },
      {
        traitId: 'rising_power',
        name: '逐渐强大',
        description: '国势日盛，军事训练速度+15%，新兵战斗力+10',
        effects: [
          { type: 'training', target: 'military', value: 15 },
          { type: 'combat', target: 'new_troops', value: 10 },
        ],
      },
    ],
    baseResources: {
      food: 3000,
      money: 1500,
      population: 10000,
      prestige: 60,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: true,
      maxCities: 0,
      uniqueBuildings: ['ritual_academy', 'agricultural_college'],
    },
    historicalBackground: {
      origin: '周始祖后稷，相传为帝喾之子，善于农耕，被舜封于邰。',
      culture: '重视农业生产，崇尚礼乐，以德治国，与商的鬼神崇拜形成对比。',
      keyFigures: ['古公亶父', '季历', '文王', '武王'],
      riseAndFall: '古公亶父迁岐奠定基础，文王积德行善，武王趁商师东征伐纣，建立周朝。',
    },
  },

  // 鬼方 - 北方游牧
  guifang: {
    templateId: 'guifang',
    baseProfile: {
      name: '鬼方',
      type: 'nomadic',
      color: '#4A4A4A', // 灰色
      emblem: '/emblems/guifang.png',
      description: '北方强大游牧民族，经常侵扰商朝边境。擅长骑射，机动性强。',
    },
    traitTemplates: [
      {
        traitId: 'nomadic_lifestyle',
        name: '游牧民族',
        description: '逐水草而居，不受城邑限制，军队机动性+30%，但无法建造固定建筑',
        effects: [
          { type: 'mobility', target: 'army', value: 30 },
          { type: 'restriction', target: 'building', value: -100 },
        ],
      },
      {
        traitId: 'master_archer',
        name: '骑射精通',
        description: '从小在马背上长大，弓骑兵战斗力+25%，射程+10%',
        effects: [
          { type: 'combat', target: 'horse_archer', value: 25 },
          { type: 'range', target: 'archer', value: 10 },
        ],
      },
      {
        traitId: 'raiding_nature',
        name: '劫掠成性',
        description: '以战养战，劫掠收益+40%，但与其他势力好感度-20',
        effects: [
          { type: 'loot', target: 'raid', value: 40 },
          { type: 'opinion', target: 'all_factions', value: -20 },
        ],
      },
      {
        traitId: 'swift_as_wind',
        name: '来去如风',
        description: '军队移动速度+25%，撤退成功率+20%',
        effects: [
          { type: 'speed', target: 'army', value: 25 },
          { type: 'retreat', target: 'success', value: 20 },
        ],
      },
    ],
    baseResources: {
      food: 2000,
      money: 800,
      population: 8000,
      prestige: 40,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: true,
      maxCities: 3, // 游牧民族城邑上限
      uniqueBuildings: ['tent_city', 'horse_breeding_ground'],
    },
    historicalBackground: {
      origin: '北方草原游牧民族，与商朝长期对立，是商朝的主要边患之一。',
      culture: '游牧文化，崇尚武力，以部落为单位，擅长骑射和游击战。',
      keyFigures: ['鬼方首领'],
      riseAndFall: '长期与商朝交战，武丁时期曾大规模征讨，部分被征服，部分北迁。',
    },
  },

  // 羌方 - 西方羌人
  qiangfang: {
    templateId: 'qiangfang',
    baseProfile: {
      name: '羌方',
      type: 'nomadic',
      color: '#6B8E6B', // 草绿色
      description: '西方羌人部落，分布广泛。与商朝时战时和，是重要的奴隶来源。',
    },
    traitTemplates: [
      {
        traitId: 'qiang_people',
        name: '羌人部落',
        description: '分布广泛的羌人，人口分散但总数众多，人口上限+20%，招募成本-15%',
        effects: [
          { type: 'cap', target: 'population', value: 20 },
          { type: 'cost', target: 'recruitment', value: -15 },
        ],
      },
      {
        traitId: 'pastoral_economy',
        name: '畜牧为主',
        description: '以畜牧业为主，畜牧产量+30%，可快速移动牧场',
        effects: [
          { type: 'production', target: 'livestock', value: 30 },
          { type: 'mobility', target: 'pasture', value: 50 },
        ],
      },
      {
        traitId: 'fierce_warriors',
        name: '骁勇善战',
        description: '羌人战士勇猛，步兵战斗力+15%，士气+10',
        effects: [
          { type: 'combat', target: 'infantry', value: 15 },
          { type: 'morale', target: 'troops', value: 10 },
        ],
      },
      {
        traitId: 'widespread_distribution',
        name: '分布广泛',
        description: '部落分散各地，难以彻底征服，被占领后叛乱几率+25%',
        effects: [
          { type: 'rebellion', target: 'chance', value: 25 },
        ],
      },
    ],
    baseResources: {
      food: 1800,
      money: 600,
      population: 10000,
      prestige: 35,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: true,
      maxCities: 4,
      uniqueBuildings: ['tribal_camp', 'livestock_market'],
    },
    historicalBackground: {
      origin: '西方羌人部落联盟，分布范围广泛，从甘肃到四川都有羌人活动。',
      culture: '游牧与农耕混合，部落分散，各部落相对独立，崇尚勇武。',
      keyFigures: ['羌方各部首领'],
      riseAndFall: '长期与商朝时战时和，商朝经常征讨羌方获取奴隶和贡品。',
    },
  },

  // 人方 - 东夷
  renfang: {
    templateId: 'renfang',
    baseProfile: {
      name: '人方',
      type: 'hostile',
      color: '#CD853F', // 秘鲁色
      description: '东夷部落，位于山东半岛和淮河流域。帝辛时期大规模征讨对象。',
    },
    traitTemplates: [
      {
        traitId: 'eastern_yi',
        name: '东夷部落',
        description: '东方强大部落联盟，海军战斗力+20%，沿海城邑防御+15%',
        effects: [
          { type: 'combat', target: 'navy', value: 20 },
          { type: 'defense', target: 'coastal_city', value: 15 },
        ],
      },
      {
        traitId: 'bronze_smelting',
        name: '青铜冶炼',
        description: '掌握青铜技术，青铜武器质量+15%，青铜产量+10%',
        effects: [
          { type: 'quality', target: 'bronze_weapon', value: 15 },
          { type: 'production', target: 'bronze', value: 10 },
        ],
      },
      {
        traitId: 'huai_river_basin',
        name: '淮河流域',
        description: '控制富庶的淮河流域，粮食产量+20%，商业收入+15%',
        effects: [
          { type: 'production', target: 'food', value: 20 },
          { type: 'income', target: 'commerce', value: 15 },
        ],
      },
      {
        traitId: 'frequent_rebellion',
        name: '频繁叛乱',
        description: '不甘臣服，叛乱倾向+30%，独立战争战斗力+20%',
        effects: [
          { type: 'rebellion', target: 'tendency', value: 30 },
          { type: 'combat', target: 'independence_war', value: 20 },
        ],
      },
    ],
    baseResources: {
      food: 2500,
      money: 1200,
      population: 12000,
      prestige: 50,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: true,
      maxCities: 0,
      uniqueBuildings: ['bronze_smelter', 'naval_dock'],
    },
    historicalBackground: {
      origin: '东夷部落联盟，分布在山东半岛和淮河流域，与中原文化不同。',
      culture: '东夷文化，擅长航海和青铜冶炼，与商朝既有交流又有冲突。',
      keyFigures: ['人方首领'],
      riseAndFall: '帝辛时期大规模征讨人方，虽然获胜但消耗巨大，间接导致商朝灭亡。',
    },
  },

  // 召方 - 臣服方国示例
  zhaofang: {
    templateId: 'zhaofang',
    baseProfile: {
      name: '召方',
      type: 'vassal',
      color: '#9370DB', // 中紫色
      description: '西方臣服方国，忠于商王室，负责西部边防。',
    },
    traitTemplates: [
      {
        traitId: 'loyal_vassal',
        name: '忠诚附庸',
        description: '对宗主忠诚，宗主好感度+20%，叛乱几率-30%',
        effects: [
          { type: 'opinion', target: 'suzerain', value: 20 },
          { type: 'rebellion', target: 'chance', value: -30 },
        ],
      },
      {
        traitId: 'western_defense',
        name: '西部边防',
        description: '负责西部边防，边境防御+20%，对游牧民族战斗力+15%',
        effects: [
          { type: 'defense', target: 'border', value: 20 },
          { type: 'combat', target: 'vs_nomadic', value: 15 },
        ],
      },
    ],
    baseResources: {
      food: 1500,
      money: 800,
      population: 6000,
      prestige: 30,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: false,
      maxCities: 0,
      uniqueBuildings: ['border_fort'],
    },
    historicalBackground: {
      origin: '西方古老方国，与商朝关系密切，负责防御西部游牧民族。',
      culture: '受商文化影响较深，同时保留地方特色，重视军事防御。',
      keyFigures: ['召方国君'],
      riseAndFall: '长期作为商的附庸存在，商亡后部分被周吸收，部分独立。',
    },
  },
}

// ============================================
// 通用模板（用于分封、叛乱生成新势力）
// ============================================

export const GENERIC_TEMPLATES: Record<string, FactionTemplate> = {
  // 普通诸侯模板
  generic_vassal: {
    templateId: 'generic_vassal',
    baseProfile: {
      name: '诸侯',
      type: 'vassal',
      color: '#808080',
      description: '普通诸侯势力，由分封或独立产生。',
    },
    traitTemplates: [
      {
        traitId: 'new_faction',
        name: '新兴势力',
        description: '新建立的势力，发展速度+10%，但基础薄弱',
        effects: [
          { type: 'growth', target: 'all', value: 10 },
        ],
      },
    ],
    baseResources: {
      food: 1000,
      money: 500,
      population: 5000,
      prestige: 20,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: true,
      maxCities: 0,
      uniqueBuildings: [],
    },
    historicalBackground: {
      origin: '由分封或独立产生的新兴势力。',
      culture: '继承母国文化，同时发展自身特色。',
      keyFigures: [],
      riseAndFall: '命运取决于君主的治理和外部环境。',
    },
  },

  // 独立势力模板
  generic_independent: {
    templateId: 'generic_independent',
    baseProfile: {
      name: '独立势力',
      type: 'independent',
      color: '#A9A9A9',
      description: '独立的地方势力，不依附于任何大国。',
    },
    traitTemplates: [
      {
        traitId: 'independent_spirit',
        name: '独立精神',
        description: '独立自主，不受约束，但外交选择较少',
        effects: [
          { type: 'freedom', target: 'policy', value: 20 },
          { type: 'diplomacy', target: 'options', value: -10 },
        ],
      },
    ],
    baseResources: {
      food: 1200,
      money: 600,
      population: 6000,
      prestige: 25,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: false,
      maxCities: 0,
      uniqueBuildings: [],
    },
    historicalBackground: {
      origin: '地方独立势力，可能是前朝遗民或地方豪强。',
      culture: '保留地方传统文化，相对封闭。',
      keyFigures: [],
      riseAndFall: '在夹缝中求生存，或被吞并，或崛起为大国。',
    },
  },

  // 叛乱势力模板
  rebel_faction: {
    templateId: 'rebel_faction',
    baseProfile: {
      name: '叛军',
      type: 'hostile',
      color: '#DC143C', // 猩红色
      description: '叛乱产生的势力，为独立而战。',
    },
    traitTemplates: [
      {
        traitId: 'rebel_cause',
        name: '反叛精神',
        description: '为自由而战，独立战争战斗力+25%，士气+20',
        effects: [
          { type: 'combat', target: 'independence_war', value: 25 },
          { type: 'morale', target: 'troops', value: 20 },
        ],
      },
      {
        traitId: 'unrecognized',
        name: '不被承认',
        description: '不被其他国家承认，外交困难，无法结盟',
        effects: [
          { type: 'diplomacy', target: 'recognition', value: -50 },
        ],
      },
    ],
    baseResources: {
      food: 800,
      money: 300,
      population: 4000,
      prestige: 15,
    },
    specialRules: {
      canBeVassalized: true,
      canDeclareIndependence: false,
      maxCities: 0,
      uniqueBuildings: [],
    },
    historicalBackground: {
      origin: '因不满统治而叛乱的势力。',
      culture: '反抗精神强烈，凝聚力高。',
      keyFigures: [],
      riseAndFall: '要么成功独立，要么被镇压。',
    },
  },
}

// ============================================
// 模板获取函数
// ============================================

export function getFactionTemplate(templateId: string): FactionTemplate | undefined {
  return SHANG_TEMPLATES[templateId] || GENERIC_TEMPLATES[templateId]
}

export function getAllShangTemplates(): FactionTemplate[] {
  return Object.values(SHANG_TEMPLATES)
}

export function getPlayableTemplates(): FactionTemplate[] {
  return Object.values(SHANG_TEMPLATES).filter(t => 
    t.templateId === 'shang' || 
    t.templateId === 'zhou' || 
    t.templateId === 'guifang' || 
    t.templateId === 'qiangfang' || 
    t.templateId === 'renfang'
  )
}

export function getGenericTemplate(type: 'vassal' | 'independent' | 'rebel'): FactionTemplate {
  const map = {
    vassal: GENERIC_TEMPLATES.generic_vassal,
    independent: GENERIC_TEMPLATES.generic_independent,
    rebel: GENERIC_TEMPLATES.rebel_faction,
  }
  return map[type]
}

// 导出所有模板
export const ALL_TEMPLATES: Record<string, FactionTemplate> = {
  ...SHANG_TEMPLATES,
  ...GENERIC_TEMPLATES,
}
